import React, {ReactElement} from 'react'
import styled from 'styled-components'
import {BlockSpacer, currentMedia} from '../../../styledComponents/ResponsiveConvenience'
import {Checkbox, FormControlLabel, FormGroup} from '@mui/material';
import {ErrorHandlingFormProps} from '../../../components/shared/forms/hoc/createErrorHandlingForm'
import {withErrorSubscribe} from '../../../components/shared/forms/hoc/withErrorSubscribe'
import {Colors, FontSize} from '../../../styledComponents/Constants'
import FormErrorMessage from '../../../components/shared/forms/FormErrorMessage'
import {DisplayItemInputTitleRequiredNoMargin} from '../../../styledComponents/DisplayItem'
import {VariableTrading} from './repository/NatGetResidentBillingAmountDetail'
import {getFormattedYearMonth} from '../common/NatDateFormatted'

interface Props {
    variableTrading: VariableTrading[],
    isAdminUser: boolean,
    onChangeToggleAll: (checkedItems: string[]) => void;
    onChangeToggleItem: (checkedItems: string[]) => void;
}

interface State {
    checkedItemName: string[]
}

class NatResidentReceiptIssueProviso extends React.Component<Props & ErrorHandlingFormProps, State> {
    constructor(props: Readonly<Props & ErrorHandlingFormProps>) {
        super(props)
        this.state = {
            checkedItemName: []
        }

        this.onChangeToggleAll = this.onChangeToggleAll.bind(this)
        this.onChangeToggleItem = this.onChangeToggleItem.bind(this)
    }

    // 但し書き：すべて選択
    onChangeToggleAll(): void {
        const {variableTrading} = this.props
        const {checkedItemName} = this.state
        const checkedItems = checkedItemName.length === variableTrading.length
            ? []
            : variableTrading.map((item) => item.expenseItemName)

        this.setState({
            checkedItemName: checkedItems
        }, () => {
            this.props.onChangeToggleAll(checkedItems)
        })
    }

    // 但し書き：選択
    onChangeToggleItem(checkedItem: string): void {
        const {checkedItemName} = this.state
        const checkedItems = checkedItemName.includes(checkedItem)
            ? checkedItemName.filter((name) => name !== checkedItem)
            : [...checkedItemName, checkedItem]

        this.setState({
            checkedItemName: checkedItems
        }, () => {
            this.props.onChangeToggleItem(checkedItems)
        })
    };

    // 選択した但し書きの合計
    calculateTotal = (): number => {
        const {checkedItemName} = this.state
        const {variableTrading} = this.props

        return variableTrading
            ? variableTrading.filter((item) => checkedItemName.includes(item.expenseItemName))
                .reduce((total, item) => total + Number(item.amount), 0)
            : 0
    };

    render(): ReactElement {
        const {variableTrading, isAdminUser, messages, hasError} = this.props
        const {checkedItemName} = this.state
        const isAllSelected = variableTrading ? checkedItemName.length === variableTrading.length : false

        return (
            <>
                <WhiteArea>
                    <DisplayItemInputTitleRequiredNoMargin>
                        {'但し書き'}
                    </DisplayItemInputTitleRequiredNoMargin>
                    <BlockSpacer height={'15px'}/>
                    <span style={{color: `${Colors.AccentDark}`}}>{checkedItemName.length} 個選択</span>
                    <BlockSpacer height={'10px'}/>
                    <StyledFormGroup $hasError={hasError}>
                        <StyledSelectAllLabel
                            label={
                                <span style={{fontSize: FontSize.Middle}}>
                                    すべて選択
                                </span>
                            }
                            control={
                                <Checkbox
                                    checked={isAllSelected}
                                    indeterminate={checkedItemName.length > 0 && !isAllSelected}
                                    onChange={this.onChangeToggleAll}
                                />
                            }
                        />
                        {variableTrading?.map((item, index) => (
                            <StyledItemLabel
                                key={index}
                                label={
                                    <>
                                        <div style={{display: 'flex'}}>
                                            <ItemNameSpan>
                                                {item.expenseItemName}
                                            </ItemNameSpan>
                                            <YearMonthSpan>
                                                {isAdminUser
                                                    ? item.accountingYearMonth
                                                    : getFormattedYearMonth(item.accountingYearMonth)}分
                                            </YearMonthSpan>
                                        </div>
                                        <BoldColumn>
                                            {isAdminUser
                                                ? item.amount
                                                : Number(item.amount).toLocaleString()}円
                                        </BoldColumn>
                                    </>
                                }
                                control={
                                    <Checkbox
                                        checked={checkedItemName.includes(item.expenseItemName)}
                                        onChange={() => this.onChangeToggleItem(item.expenseItemName)}
                                    />
                                }
                            />
                        ))}
                    </StyledFormGroup>
                    <InputField>
                        {
                            messages &&
                            <ErrorMessage
                                messages={messages.map((pair) => {
                                    return {
                                        message: pair.message,
                                    }
                                })}
                            />
                        }
                    </InputField>
                </WhiteArea>
                <BlockSpacer height={'1px'}/>
                <WhiteArea>
                    <TotalContainer>
                        <TotalLabel>合計</TotalLabel>
                        <BoldColumn $extraBig={true}>
                            {isAdminUser
                                ? 'ー'
                                : this.calculateTotal().toLocaleString()}円</BoldColumn>
                    </TotalContainer>
                </WhiteArea>
            </>
        )
    }
}

export default withErrorSubscribe(NatResidentReceiptIssueProviso)

const WhiteArea = styled.div`
    background-color: ${Colors.Surface};
    ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
    padding: 15px;
    box-sizing: border-box;
    overflow: hidden;
`

const StyledSelectAllLabel = styled(FormControlLabel)`
    border-top: 1px solid ${Colors.AccentLight};
    border-bottom: 1px solid ${Colors.AccentLight};
    background-color: rgba(241, 241, 241, 1);

    &.MuiFormControlLabel-root {
        margin: 0;
    }

    & .MuiTypography-root {
        font-size: ${FontSize.Body};
        color: ${Colors.Body};
    }

    & .MuiSvgIcon-root {
        font-size: ${FontSize.Big};
    }

    & .MuiCheckbox-root {
        color: rgba(0, 0, 0, 0.56)
    }

    & .MuiCheckbox-root.Mui-checked,
    & .MuiCheckbox-indeterminate {
        color: rgba(25, 118, 210, 1)
    }
`

const StyledItemLabel = styled(FormControlLabel)`
    border-bottom: 1px solid ${Colors.AccentLight};

    &.MuiFormControlLabel-root {
        margin: 0;
    }

    & .MuiTypography-root {
        font-size: ${FontSize.Middle};
        color: ${Colors.Body};
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    & .MuiSvgIcon-root {
        font-size: ${FontSize.Big};
    }

    & .MuiCheckbox-root {
        color: rgba(0, 0, 0, 0.56)
    }

    & .MuiCheckbox-root.Mui-checked {
        color: rgba(25, 118, 210, 1)
    }
`

const ItemNameSpan = styled.span`
    width: 132px;
    gap: 5px;
`

const YearMonthSpan = styled.span`
    font-size: 10px;
    color: rgba(112, 112, 112, 1);
`

const BoldColumn = styled.div<{ $extraBig?: boolean }>`
    font-size: ${({$extraBig}) => ($extraBig ? FontSize.ExtraBig : FontSize.Middle)};
    text-align: right;
    font-weight: bold;
    ${props => !props.$extraBig && `
    padding-right: 15px;
   `}
`

const TotalContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const TotalLabel = styled.div`
    font-size: ${FontSize.Accent};
    color: ${Colors.Body};
    padding: 9px 0 9px 15px;
    font-weight: bold;
`

const StyledFormGroup = styled(FormGroup)<{ $hasError?: boolean }>`
    ${props => props.$hasError
            ? `outline: 1px solid rgb(215, 55, 74);
             background-color: rgb(253,245,246);`
            : ''}
`

const ErrorMessage = styled(FormErrorMessage)`
    margin-left: 14px !important;
    text-align: left !important;
    width: 345px;
    font-size: ${FontSize.Small} !important;
    font-family: "Noto Sans JP", sans-serif !important;
    color: ${Colors.Alert} !important;
`

const InputField = styled.div`
    display: flex;
    margin: auto;
`
